import React, { useState, useEffect, useCallback, useMemo } from 'react';
import type { Student, ClassRoom } from '../../types/db';
import {
  Calendar as CalendarIcon,
  Save,
  Loader2,
  Printer,
  CheckCircle2,
  Layers,
} from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../context/AuthContext';

export interface AttendanceManagerProps {
  students: Student[];
  branchId?: string;
}

export function AttendanceManager({ students, branchId }: AttendanceManagerProps) {
  const { profile: currentUser } = useAuth();
  const [classes, setClasses] = useState<ClassRoom[]>([]);
  const [selectedClassId, setSelectedClassId] = useState<string>('ALL'); // Default ke SEMUA KELAS
  
  // State Tanggal ISO (YYYY-MM-DD)
  const [sessionDate, setSessionDate] = useState(new Date().toISOString().split('T')[0]);

  const [records, setRecords] = useState<Record<string, 'H' | 'I' | 'S' | 'A'>>({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);

  // Helper Format Tampilan Tanggal Indonesia (DD/MM/YYYY)
  const formatDateID = (dateString: string) => {
    if (!dateString) return '-';
    const parts = dateString.split('-');
    if (parts.length !== 3) return dateString;
    const [year, month, day] = parts;
    return `${day}/${month}/${year}`;
  };

  // 1. Fetch Daftar Kelas
  const fetchClasses = useCallback(async () => {
    let query = supabase.from('classes').select('*').order('name');
    
    const activeBranch = branchId || currentUser?.branch_id;
    if (activeBranch && activeBranch !== 'HEADQUARTERS' && activeBranch !== 'ALL') {
      query = query.eq('branch_id', activeBranch);
    }

    const { data } = await query;
    if (data && data.length > 0) {
      setClasses(data);
    } else {
      setClasses([]);
    }
  }, [currentUser, branchId]);

  useEffect(() => {
    fetchClasses();
  }, [fetchClasses]);

  // Filter siswa: Jika ALL, tampilkan seluruh siswa cabang aktif
  const filteredStudents = useMemo(() => {
    return students.filter((s) => {
      if (s.is_active === false) return false;
      if (selectedClassId === 'ALL') return true;
      return s.class_id === selectedClassId;
    });
  }, [students, selectedClassId]);

  // 2. Fetch Records Presensi
  useEffect(() => {
    let isSubscribed = true;

    const loadData = async () => {
      if (filteredStudents.length === 0) {
        setRecords({});
        return;
      }

      setFetching(true);
      try {
        const studentIds = filteredStudents.map((s) => s.id);
        const { data, error } = await supabase
          .from('attendance_records')
          .select('*')
          .eq('date', sessionDate)
          .in('student_id', studentIds);

        if (error) throw error;

        if (isSubscribed) {
          const newRecs: Record<string, 'H' | 'I' | 'S' | 'A'> = {};
          data?.forEach((r) => {
            newRecs[r.student_id] = r.status as any;
          });
          setRecords(newRecs);
        }
      } catch (err: any) {
        /* error handled silently */
      } finally {
        if (isSubscribed) setFetching(false);
      }
    };

    loadData();

    return () => {
      isSubscribed = false;
    };
  }, [selectedClassId, sessionDate, filteredStudents]);

  const setStatus = (studentId: string, status: 'H' | 'I' | 'S' | 'A') => {
    setRecords((prev) => ({ ...prev, [studentId]: status }));
  };

  const handleMarkAllPresent = () => {
    const updated = { ...records };
    filteredStudents.forEach((s) => {
      updated[s.id] = 'H';
    });
    setRecords(updated);
  };

  const handleSave = async () => {
    if (filteredStudents.length === 0) return;
    setLoading(true);
    try {
      const payload = filteredStudents.map((s) => ({
        student_id: s.id,
        branch_id: s.branch_id,
        date: sessionDate,
        status: records[s.id] || 'H',
      }));

      const { error } = await supabase
        .from('attendance_records')
        .upsert(payload, { onConflict: 'student_id, date' });

      if (error) throw error;

      window.alert(`Presensi Kelas Berhasil Disimpan! (${payload.length} Siswa)`);
    } catch (err: any) {
      window.alert('Gagal Menyimpan Presensi: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const activeClass = classes.find((c) => c.id === selectedClassId);
    const classNameText = selectedClassId === 'ALL' ? 'SEMUA KELAS / PARALEL' : (activeClass?.name || '-');
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <html>
        <head>
          <title>Presensi Kelas - ${classNameText}</title>
          <script src="https://cdn.tailwindcss.com"></script>
        </head>
        <body class="p-10 font-sans">
          <div class="border-b pb-4 mb-6">
            <h1 class="text-2xl font-black uppercase">REKAP PRESENSI KELAS</h1>
            <p class="text-sm">Kelas: ${classNameText} | Tanggal Sesi: ${formatDateID(sessionDate)}</p>
          </div>
          <table class="w-full text-left border-collapse border border-slate-200">
            <thead>
              <tr class="bg-slate-100 text-xs font-bold uppercase">
                <th class="p-3 border">No</th>
                <th class="p-3 border">Nama Siswa</th>
                <th class="p-3 border">Status Kehadiran</th>
              </tr>
            </thead>
            <tbody>
              ${filteredStudents
                .map(
                  (s, idx) => `
                <tr class="border text-sm">
                  <td class="p-3 border">${idx + 1}</td>
                  <td class="p-3 border font-bold uppercase">${s.name}</td>
                  <td class="p-3 border font-bold">${
                    records[s.id] === 'H'
                      ? 'HADIR'
                      : records[s.id] === 'I'
                      ? 'IZIN'
                      : records[s.id] === 'S'
                      ? 'SAKIT'
                      : records[s.id] === 'A'
                      ? 'ALFA'
                      : '-'
                  }</td>
                </tr>
              `
                )
                .join('')}
            </tbody>
          </table>
        </body>
      </html>
    `);
    printWindow.document.close();
    window.setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  return (
    <div className="space-y-8 animate-fade-in font-sans pb-20">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div>
          <h2 className="text-2xl font-black text-gray-900 uppercase tracking-tight">
            Attendance Management
          </h2>
          <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">
            Presensi Terintegrasi Cabang & Kelas
          </p>
        </div>
      </div>

      <div className="bg-white p-6 md:p-10 rounded-[3rem] border border-slate-50 shadow-sm min-h-[500px] space-y-8">
        
        {/* ROW FILTER: DROPDOWN DENGAN OPSI SEMUA KELAS */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 border-b border-slate-100 pb-8 items-end">
          
          {/* SELECT KELAS */}
          <div className="lg:col-span-4 space-y-2">
            <label className="text-[10px] font-black text-indigo-600 uppercase tracking-widest flex items-center gap-2">
              <Layers className="w-3.5 h-3.5" /> Pilih Kelas Aktif
            </label>
            <select
              className="w-full border-2 border-slate-200 px-4 py-3 rounded-2xl text-xs font-black uppercase bg-white text-slate-900 outline-none focus:border-indigo-500 transition-all cursor-pointer h-[52px] appearance-auto"
              value={selectedClassId}
              onChange={(e) => setSelectedClassId(e.target.value)}
            >
              <option value="ALL" className="text-indigo-600 font-black py-2">
                -- SEMUA KELAS / PARALEL --
              </option>
              {classes.map((c) => (
                <option key={c.id} value={c.id} className="text-slate-900 font-bold py-2">
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          {/* INPUT KALENDER NATIVE */}
          <div className="lg:col-span-3 space-y-2">
            <label className="text-[10px] font-black text-indigo-600 uppercase tracking-widest flex items-center gap-2">
              <CalendarIcon className="w-3.5 h-3.5" /> Tanggal ({formatDateID(sessionDate)})
            </label>
            <input
              type="date"
              className="w-full border-2 border-slate-200 px-4 py-3 rounded-2xl text-xs font-black bg-white text-slate-900 outline-none focus:border-indigo-500 transition-all h-[52px]"
              value={sessionDate}
              onChange={(e) => setSessionDate(e.target.value)}
            />
          </div>

          {/* TOMBOL PINTAS & AKSI */}
          <div className="lg:col-span-5 flex items-center gap-2">
            {filteredStudents.length > 0 && (
              <button
                type="button"
                onClick={handleMarkAllPresent}
                className="flex-1 px-4 py-3 rounded-2xl font-black text-[9px] uppercase tracking-wider text-emerald-600 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 transition-all flex items-center justify-center gap-1.5 h-[52px]"
              >
                <CheckCircle2 className="w-4 h-4 shrink-0" /> Semuanya Hadir
              </button>
            )}

            <button
              type="button"
              onClick={handlePrint}
              disabled={filteredStudents.length === 0}
              className="p-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-2xl transition-all disabled:opacity-50 h-[52px] w-[52px] shrink-0 flex items-center justify-center"
              title="Cetak Presensi"
            >
              <Printer className="w-5 h-5" />
            </button>

            <button
              type="button"
              onClick={handleSave}
              disabled={loading || filteredStudents.length === 0}
              className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-black text-xs uppercase tracking-widest rounded-2xl shadow-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50 h-[52px] shrink-0"
            >
              {loading ? <Loader2 className="animate-spin w-4 h-4" /> : <Save className="w-4 h-4" />}
              Simpan
            </button>
          </div>
        </div>

        {/* LIST SISWA KELAS */}
        {fetching ? (
          <div className="py-24 text-center flex flex-col items-center justify-center gap-3">
            <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
              Memuat Data Presensi...
            </p>
          </div>
        ) : filteredStudents.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredStudents.map((s) => {
              const currentStatus = records[s.id];
              return (
                <div
                  key={s.id}
                  className="bg-slate-50 p-6 rounded-3xl border border-slate-100 flex items-center justify-between gap-4 hover:bg-white hover:shadow-sm transition-all"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-indigo-600 text-white rounded-xl flex items-center justify-center font-black text-xs shadow-md">
                      {s.name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-black text-slate-800 uppercase text-xs">{s.name}</p>
                      <p className="text-[8px] text-slate-400 font-bold uppercase mt-0.5">
                        {s.program_choice || 'REGULER'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5">
                    {[
                      { k: 'H', l: 'HADIR', c: 'bg-emerald-500 text-white' },
                      { k: 'I', l: 'IZIN', c: 'bg-amber-500 text-white' },
                      { k: 'S', l: 'SAKIT', c: 'bg-blue-500 text-white' },
                      { k: 'A', l: 'ALFA', c: 'bg-rose-500 text-white' },
                    ].map((st) => (
                      <button
                        key={st.k}
                        type="button"
                        onClick={() => setStatus(s.id, st.k as any)}
                        className={`px-3.5 py-2 rounded-xl text-[9px] font-black transition-all border ${
                          currentStatus === st.k
                            ? `${st.c} border-transparent shadow-sm`
                            : 'bg-white border-slate-200 text-slate-400 hover:border-indigo-300'
                        }`}
                      >
                        {st.k}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="py-20 text-center flex flex-col items-center justify-center bg-slate-50 rounded-3xl border-2 border-dashed border-slate-100">
            <Layers className="w-12 h-12 text-slate-200 mb-2" />
            <p className="text-slate-400 font-black uppercase text-xs">
              Belum ada siswa terdaftar di cabang ini
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
